import React, { useState, useEffect, useRef } from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import GroupIcon from "@mui/icons-material/Group";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import Button from "@mui/material/Button";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import { styled } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import BookmarkButton from "../Bookmarks/BookmarkButton";
import axios from "axios";
import useApp from "../../store/contexts/AppContext";
import { USER_ACCOUNT_TYPES } from "../../constants/types";
import CUSTOM_EVENTS from "../../constants/customEvents";
import { ROUTES } from "../../routes/User/paths";
import FormatNumberCounter from "../../utils/FormatNumberCounter";

const TrendingContainer = styled(Paper)(({ theme }) => ({
  borderRadius: "16px",
  overflow: "hidden",
  backgroundColor: theme.palette.mode === "dark" ? "#1a1a1f" : "#ffffff",
  border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(212,68,1,0.15)"}`,
  marginTop: "20px",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 4px 20px rgba(0, 0, 0, 0.3)"
      : "0 4px 20px rgba(212, 68, 1, 0.08)",
  height: "auto",
  minHeight: "320px",
  transition: "all 0.3s ease",
}));

const TrendingHeader = styled(Box)(({ theme }) => ({
  padding: "16px 20px",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
  backgroundColor: theme.palette.mode === "dark" ? "#1e1e24" : "#fafafa",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const TrendingTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "1rem",
  color: theme.palette.mode === "dark" ? "#fff" : "#1a1a1f",
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

const TrendingList = styled(List)(({ theme }) => ({
  padding: 0,
  backgroundColor: theme.palette.mode === "dark" ? "#1a1a1f" : "#ffffff",
}));

const TrendingListItem = styled(ListItem)(({ theme, isfirst }) => ({
  padding: "14px 20px",
  transition: "all 0.2s ease",
  cursor: "pointer",
  backgroundColor:
    isfirst === "true"
      ? theme.palette.mode === "dark"
        ? "rgba(212, 68, 1, 0.08)"
        : "rgba(212, 68, 1, 0.04)"
      : "transparent",
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(212, 68, 1, 0.12)"
        : "rgba(212, 68, 1, 0.06)",
  },
}));

const RoomName = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: "0.9rem",
  color: theme.palette.mode === "dark" ? "#e8e8e8" : "#2d2d2d",
}));

const ParticipantCount = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  color: theme.palette.mode === "dark" ? "#888" : "#666",
  fontSize: "0.8rem",
  marginTop: "4px",
}));

const HotBadge = styled(Chip)(({ theme }) => ({
  height: "20px",
  fontSize: "0.7rem",
  fontWeight: 600,
  backgroundColor: "#d44402",
  color: "#fff",
  "& .MuiChip-label": {
    padding: "0 8px",
  },
}));

const NewBadge = styled(Chip)(({ theme }) => ({
  height: "20px",
  fontSize: "0.7rem",
  fontWeight: 600,
  backgroundColor: theme.palette.mode === "dark" ? "#4a4a5a" : "#e8e8e8",
  color: theme.palette.mode === "dark" ? "#fff" : "#333",
  "& .MuiChip-label": {
    padding: "0 8px",
  },
}));

const VIPBadge = styled(Chip)(({ theme }) => ({
  height: "20px",
  fontSize: "0.7rem",
  fontWeight: 600,
  backgroundColor: "#ffd700",
  color: "#1a1a1f",
  "& .MuiChip-label": {
    padding: "0 8px",
  },
}));

const TrendingFooter = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1e1e24" : "#fafafa",
  padding: "8px 16px",
  borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
}));

const ShowMoreButton = styled(Button)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? "#888" : "#666",
  textTransform: "none",
  fontSize: "0.85rem",
  fontWeight: 500,
  "&:hover": {
    backgroundColor: "transparent",
    color: "#d44402",
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "50px 20px",
  color: "#999",
  textAlign: "center",
  minHeight: "200px",
}));

const LoadingState = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "50px",
});

const getTierColor = (tier) => {
  const tierValue = typeof tier === "object" ? tier?.value : tier;
  switch (tierValue) {
    case USER_ACCOUNT_TYPES.PREMIUM:
      return "#d44402";
    case USER_ACCOUNT_TYPES.ELITE:
      return "#aa00ff";
    case USER_ACCOUNT_TYPES.STANDARD:
      return "#1976d2";
    default:
      return "#666";
  }
};

const getAvatarColor = (index) => {
  const colors = [
    "#d44402",
    "#1976d2",
    "#388e3c",
    "#aa00ff",
    "#00897b",
    "#f57c00",
  ];
  return colors[index % colors.length];
};

const TrendingRooms = () => {
  const { headers, socket } = useApp();
  const history = useHistory();
  const [trendingRooms, setTrendingRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayCount, setDisplayCount] = useState(5);
  const [hasMore, setHasMore] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  const initialDisplayCount = 5;

  const fetchTrendingRooms = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/chat-rooms/trending-rooms?limit=20`,
        { headers },
      );

      if (response.data?.data?.trending_rooms) {
        setTrendingRooms(response.data.data.trending_rooms);
        setHasMore(
          response.data.data.trending_rooms.length > initialDisplayCount,
        );
      }
    } catch (err) {
      console.error("Error fetching trending rooms:", err);
      setError("Failed to load trending rooms");
    } finally {
      setLoading(false);
    }
  };

  // Track component visibility using Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1, // Trigger when at least 10% of component is visible
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  // Initial fetch — runs once on mount only.
  // headers is NOT a stable dep (new object every render), so we exclude it here.
  // The 60s interval below handles re-fetching while the component is visible.
  useEffect(() => {
    fetchTrendingRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll every 60 seconds ONLY when component is visible.
  // headers excluded — it's a new object every render and would reset the interval constantly.
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(fetchTrendingRooms, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  // Listen for room participant updates via socket
  useEffect(() => {
    if (!socket) return;

    const handleParticipantUpdate = (data) => {
      if (!data?.room_id) return;

      setTrendingRooms((prev) =>
        prev.map((room) => {
          if (room.id?.toString() === data.room_id?.toString()) {
            return {
              ...room,
              participant_count:
                data.participant_count ?? room.participant_count,
            };
          }
          return room;
        }),
      );
    };

    socket.on("room_participant_updated", handleParticipantUpdate);

    return () => {
      socket.off("room_participant_updated", handleParticipantUpdate);
    };
  }, [socket]);

  // Listen for bookmark changes via custom browser event from AppContext
  useEffect(() => {
    const handleBookmarkChange = (event) => {
      const { roomId, bookmarked } = event.detail;

      setTrendingRooms((prev) =>
        prev.map((room) => {
          if (room.id?.toString() === roomId?.toString()) {
            return {
              ...room,
              is_bookmarked: bookmarked,
            };
          }
          return room;
        }),
      );
    };

    window.addEventListener(
      CUSTOM_EVENTS.ROOM_BOOKMARK_CHANGED,
      handleBookmarkChange,
    );

    return () => {
      window.removeEventListener(
        CUSTOM_EVENTS.ROOM_BOOKMARK_CHANGED,
        handleBookmarkChange,
      );
    };
  }, []);

  const handleShowMore = () => {
    setDisplayCount(trendingRooms.length);
    setHasMore(false);
  };

  const handleShowLess = () => {
    setDisplayCount(initialDisplayCount);
    setHasMore(trendingRooms.length > initialDisplayCount);
  };

  const handleRoomClick = (roomId) => {
    history.push(`${ROUTES.rooms}/${roomId}`);
  };

  const formatTimeAgo = (createdAt) => {
    if (!createdAt) return "";
    const diff = Date.now() - new Date(createdAt).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const isNew = (createdAt) => {
    if (!createdAt) return false;
    const diff = Date.now() - new Date(createdAt).getTime();
    return diff < 3600000; // Less than 1 hour
  };

  const isHot = (trendingScore) => {
    return trendingScore > 30;
  };

  const renderBadges = (room, index) => {
    const badges = [];

    if (isHot(room.trending_score)) {
      badges.push(
        <HotBadge
          key="hot"
          icon={<WhatshotIcon style={{ fontSize: 12, color: "#fff" }} />}
          label="Hot"
          size="small"
        />,
      );
    }

    if (isNew(room.created_at)) {
      badges.push(<NewBadge key="new" label="New" size="small" />);
    }

    if (room.tags?.includes("vip") || room.tags?.includes("elite")) {
      badges.push(<VIPBadge key="vip" label="VIP" size="small" />);
    }

    return badges.length > 0 ? (
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
        {badges}
      </Box>
    ) : null;
  };

  return (
    <TrendingContainer elevation={0} ref={containerRef}>
      <TrendingHeader>
        <TrendingTitle>
          <WhatshotIcon sx={{ color: "#d44402", fontSize: 20 }} />
          Trending Rooms
        </TrendingTitle>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontSize: "0.75rem" }}
        >
          Live
        </Typography>
      </TrendingHeader>

      {loading ? (
        <LoadingState>
          <CircularProgress size={24} sx={{ color: "#d44402" }} />
        </LoadingState>
      ) : error ? (
        <EmptyState>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
        </EmptyState>
      ) : trendingRooms.length === 0 ? (
        <EmptyState>
          <GroupIcon sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            No trending rooms right now
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
            Be the first to create one!
          </Typography>
        </EmptyState>
      ) : (
        <>
          <TrendingList>
            {trendingRooms.slice(0, displayCount).map((room, index) => (
              <React.Fragment key={room.id}>
                <TrendingListItem
                  isfirst={index === 0 ? "true" : "false"}
                  onClick={() => handleRoomClick(room.id)}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={room.host?.profile_picture}
                      sx={{
                        bgcolor: room.host?.profile_picture
                          ? "transparent"
                          : getAvatarColor(index),
                        width: 40,
                        height: 40,
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        border: room.host?.account_type
                          ? `2px solid ${getTierColor(room.host?.account_type)}`
                          : "none",
                      }}
                    >
                      {room.name?.charAt(0)?.toUpperCase() || "?"}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    disableTypography
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <RoomName>{room.name}</RoomName>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <ParticipantCount>
                            <GroupIcon sx={{ fontSize: "0.8rem" }} />
                            {FormatNumberCounter(
                              room.participant_count || 0,
                            )}{" "}
                            members
                          </ParticipantCount>
                          {room.created_at && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.disabled",
                                fontSize: "0.7rem",
                              }}
                            >
                              {formatTimeAgo(room.created_at)}
                            </Typography>
                          )}
                        </Box>
                        {renderBadges(room, index)}
                      </Box>
                    }
                  />
                  <BookmarkButton roomId={room.id} size="small" />
                </TrendingListItem>
                {index < Math.min(displayCount, trendingRooms.length) - 1 && (
                  <Divider sx={{ mx: 2 }} />
                )}
              </React.Fragment>
            ))}
          </TrendingList>

          {trendingRooms.length > initialDisplayCount && (
            <TrendingFooter>
              <ShowMoreButton
                fullWidth
                size="small"
                onClick={
                  displayCount === initialDisplayCount
                    ? handleShowMore
                    : handleShowLess
                }
                endIcon={
                  displayCount === initialDisplayCount ? (
                    <KeyboardArrowRightIcon />
                  ) : null
                }
              >
                {displayCount === initialDisplayCount
                  ? `Show ${trendingRooms.length - initialDisplayCount} more`
                  : "Show less"}
              </ShowMoreButton>
            </TrendingFooter>
          )}
        </>
      )}
    </TrendingContainer>
  );
};

export default TrendingRooms;
